import { cancel, select, takeEvery } from 'redux-saga/effects';
import {
  DruckRequestPayload,
  getGeschaeftsart,
  GlobalAction,
  GlobalActions,
  GlobalPut,
} from '@ps20-frontend/shop-integration-redux';
import {
  App,
  mapLeistungen,
  mapToBffVorvertragsdatenDruck,
  numberToGermanNumberFormattedString,
  Produktlinie,
} from '@ps20-frontend/frontend-models';
import {
  Feature,
  SpartenType,
  ThhPferdDruckdaten,
  ThhPferdVersichertesTierDruck,
  Tiergroesse,
  VorschaedenDruck,
} from '@ps20/bff-models';
import { PS20Logger } from '@ps20-frontend/logging';
import { getProduktlinienDruck, mapGefahren, mapLinienpreis } from '@ps20-frontend/druckdaten-lib';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { mapGesamtbeitragIfJaehrlichAndBeitragIsWrong } from '@ps20-frontend/preis-lib';
import { selectFeatureToggle } from '@ps20-frontend/selector';
import { selectSelectedProduktlinie } from '../../app/shared/selectors/produkt.selector';
import { IAppState } from '../appState';
import { Vorschaeden } from '../reducers/vorschaeden.reducer';
import { selectVorschaeden } from '../../app/shared/selectors/vorschaeden.selector';
import {
  AnzahlTiereEnum,
  Stockmass,
  Versicherungsnehmer,
} from '../reducers/versicherungsnehmer.reducer';

export function* watchDruckdatenChanged() {
  yield takeEvery(
    GlobalActions.DRUCKDATEN_REQUESTED,
    safeSaga(onDruckdatenRequested, App.THH_PFERD),
  );
}

export function* onDruckdatenRequested(action: GlobalAction<DruckRequestPayload>) {
  try {
    const state: IAppState = yield select();
    const appIds: string[] = action.payload.appIds;

    if (!appIds.includes(App.THH_PFERD)) {
      yield cancel();
      return;
    }

    const thhPferdDruckdaten: ThhPferdDruckdaten = mapDruckdaten(state);
    GlobalPut.getInstance().put(new GlobalActions().druckdatenGenerated(thhPferdDruckdaten));
  } catch (e) {
    PS20Logger.error(App.THH_PFERD, e as Error, 'Druckdatensaga.onDruckdatenRequested()');
    yield cancel();
  }
}

function getVersicherteTiereDataFromStrecke(
  versicherungsnehmer: Versicherungsnehmer,
): ThhPferdVersichertesTierDruck[] {
  const anzahlTiereEnumToStockmassMapper = {
    [AnzahlTiereEnum.EINES]: [versicherungsnehmer.stockmass],
    [AnzahlTiereEnum.MEHRERE]: versicherungsnehmer.stockmassMehrerePferde,
  };
  const pferdeStockmassListe = anzahlTiereEnumToStockmassMapper[versicherungsnehmer.anzahlTiere];

  return pferdeStockmassListe.map(stockmass => ({
    chipnummer: null,
    geschlecht: null,
    geburtsdatum: null,
    lebensnummer: null,
    tiername: null,
    tiergroesse: convertStockmassToTiergroesse(stockmass),
    rasse: null,
  } as ThhPferdVersichertesTierDruck));
}

function convertStockmassToTiergroesse(stockmass: Stockmass): Tiergroesse {
  return {
    [Stockmass.GROESSER]: Tiergroesse.GROSSPFERD,
    [Stockmass.KLEINER]: Tiergroesse.KLEINPFERD,
  }[stockmass];
}

function mapDruckdaten(state: IAppState): ThhPferdDruckdaten {
  const vorschaeden: Vorschaeden = selectVorschaeden(state);
  const ausgewaehlteProduktlinie: Produktlinie = selectSelectedProduktlinie(state);
  const vorteilsRabattActivated = selectFeatureToggle(state, Feature.VORTEILSRABATT);
  return {
    zusatzoptionenDruck: [],
    antragsnummer: state.antragsnummer,
    longText: ausgewaehlteProduktlinie.langtext,
    shortText: ausgewaehlteProduktlinie.kurztext,
    rbdAktuell: state.taasInfo.rbdNachlass,
    vorteilsRabatt: vorteilsRabattActivated ? state.taasInfo.vorteilsrabatt || 0 : undefined,
    angebotsdatum: vorteilsRabattActivated ? state.vertragsoptionen.angebotsdatum : undefined,
    zaehlstuecke: state.rabattstufe,
    treuebonus: state.taasInfo.tbNachlass,
    spartenType: SpartenType.TIERHALTERHAFTPFLICHT_PFERD,
    gesamtbeitrag: numberToGermanNumberFormattedString(
      mapGesamtbeitragIfJaehrlichAndBeitragIsWrong(
        state.vertragsoptionen.zahlungsweise,
        state.gesamtbeitragMitRabatt,
        state.jahresbeitrag,
        App.THH_PFERD,
      ),
    ),
    selbstbeteiligung: state.vertragsoptionen.selbstbeteiligung,
    linienpreis: mapLinienpreis(state.produkt),
    leistungen: mapLeistungen(ausgewaehlteProduktlinie, SpartenType.TIERHALTERHAFTPFLICHT_PFERD),
    vorschaedenDruck: mapVorschaeden(vorschaeden),
    vorversicherungenDruck: null,
    gefahrenDruck: mapGefahren(state.produkt),
    produktlinien: getProduktlinienDruck(state.produkt),
    zuersGefaehrdungsklasse: '',
    vorvertragsnummern: mapToBffVorvertragsdatenDruck(state.vertragsoptionen.vorvertragsNummern),
    vorvertragGekuendigt: state.vertragsoptionen.vorvertragGekuendigt,
    abweichungVomZielmarkt: false,
    versicherteTiere: getVersicherteTiereDataFromStrecke(state.versicherungsnehmer),
    ...druckdatenProvidedInCheckout,
    beibehalten: state.vertragsoptionen.beibehalten,
    nachlass: ausgewaehlteProduktlinie.nachlass,
    kooperation: state.vertragsoptionen.selectedKooperation,
    geschaeftsart: getGeschaeftsart(state.vertragsoptionen),
  };
}

const druckdatenProvidedInCheckout: Pick<ThhPferdDruckdaten, 'vertragsdauer' | 'beginn' | 'ablauf'> = {
  vertragsdauer: null,
  beginn: null,
  ablauf: null,
};

function mapVorschaeden(vorschaden: Vorschaeden): VorschaedenDruck {
  return {
    vorhanden: true,
    haftpflicht: vorschaden.thhPferdVorschaeden,
  };
}
